import React from 'react';
import clsx from 'clsx';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { IconName } from '../SVGIcon/SVGIcon.types';
import { VideoPlayer } from '../VideoPlayer';
import { Button } from '../Button';
import type { ReportAddonsProps } from './ReportAddons.types';
import type {
  AddonType,
  MappedAddon,
  MappedAddonAttachment,
} from '@/api/mappers/addons.mapper';

const downloadFile = async (url: string, filename?: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename || '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Prezentacja nagłówka karty w zależności od typu dodatku — ikona w kafelku
 * oraz opcjonalny opis (podpis). Tytuł domyślnie pochodzi z `typeDisplayName`,
 * ale można go nadpisać, aby był zgodny z projektem w Figmie.
 */
interface AddonPresentation {
  icon: IconName;
  iconBg: string;
  iconFill: string;
  title?: string;
  description?: string;
}

const DEFAULT_PRESENTATION: AddonPresentation = {
  icon: 'paper',
  iconBg: 'bg-violet-50',
  iconFill: 'primary',
};

const ADDON_PRESENTATION: Partial<Record<AddonType, AddonPresentation>> = {
  INSPECTION_VIDEO: {
    icon: 'video-camera',
    iconBg: 'bg-violet-50',
    iconFill: 'primary',
    title: 'Film wideo z oględzin',
    description: 'Zobacz i usłysz swoje przyszłe auto bezpośrednio na filmie.',
  },
  VEHICLE_VALUATION_DOCUMENT: {
    icon: 'document-pdf',
    iconBg: 'bg-violet-50',
    iconFill: 'primary',
  },
  VEHICLE_ESTIMATION_DOCUMENT: {
    icon: 'document-pdf',
    iconBg: 'bg-violet-50',
    iconFill: 'primary',
  },
};

const getAddonPresentation = (type: AddonType): AddonPresentation =>
  ADDON_PRESENTATION[type] ?? DEFAULT_PRESENTATION;

/**
 * Złoty przycisk pobierania (kolory wg projektu: tło #ca9a39, biały tekst).
 * Ikonę dokłada się przez prop `icon`.
 */
const DownloadButton: React.FC<{
  url?: string;
  filename?: string;
  label: string;
}> = ({ url, filename, label }) => (
  <Button
    variant="gold"
    size="medium"
    onClick={() => url && downloadFile(url, filename)}
    disabled={!url}
    aria-label={label}
    className="shrink-0 self-start sm:self-auto"
    iconPosition='right'
    icon={<SVGIcon name='download-arrow' fill='white' className='ml-4' size={'md'} />}
  >
    {label}
  </Button >
);

const AddonAttachmentVideo: React.FC<{
  attachment: MappedAddonAttachment;
  vehicleDescription?: string;
}> = ({ attachment, vehicleDescription }) => (
  <VideoPlayer
    videoUrl={attachment.previewUrl!}
    contentType={attachment.contentType}
    sectionTitle="Film z oględzin pojazdu"
    vehicleDescription={vehicleDescription}
  />
);

/**
 * Parametry wbudowanego czytnika PDF przeglądarki, które ukrywają górny pasek
 * menu (`toolbar`), boczne panele z miniaturami (`navpanes`) oraz dopasowują
 * dokument do szerokości (`view=FitH`), eliminując czarne pasy po bokach.
 */
const PDF_VIEWER_PARAMS = '#toolbar=0&navpanes=0&scrollbar=0&view=FitH';

const withPdfViewerParams = (url?: string): string | undefined =>
  url ? `${url}${PDF_VIEWER_PARAMS}` : url;

const isInlinePdfPreviewSupported = (): boolean =>
  typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === true;

const PDF_PREVIEW_WRAPPER_CLASS =
  'relative h-[70vh] max-h-[680px] min-h-[420px] w-full overflow-auto rounded-2xl sm:h-[80vh] sm:max-h-[1100px] sm:min-h-[600px] sm:overflow-hidden';

const PDF_PREVIEW_IFRAME_CLASS =
  'block h-full w-[720px] max-w-none border-0 bg-white sm:absolute sm:inset-0 sm:w-full sm:min-w-0';

const AddonAttachmentDocument: React.FC<{
  attachment: MappedAddonAttachment;
}> = ({ attachment }) => {
  const previewUrl = attachment.previewUrl;

  if (!previewUrl) {
    return null;
  }

  if (!isInlinePdfPreviewSupported()) {
    return (
      <div className="flex w-full justify-center">
        <Button
          as="a"
          variant="gold"
          size="medium"
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Otwórz załącznik w nowym oknie
        </Button>
      </div>
    );
  }

  return (
    <div className={PDF_PREVIEW_WRAPPER_CLASS}>
      <iframe
        src={withPdfViewerParams(previewUrl)}
        title={attachment.attachmentTypeDisplayName || 'Podgląd PDF'}
        className={PDF_PREVIEW_IFRAME_CLASS}
      />
    </div>
  );
};

const AddonAttachmentImage: React.FC<{
  attachment: MappedAddonAttachment;
}> = ({ attachment }) => (
  <div className="flex w-full justify-center">
    <img
      src={attachment.previewUrl}
      alt={
        attachment.attachmentTypeDisplayName ||
        attachment.displayName ||
        'Skan dokumentu'
      }
      className="h-auto max-h-[80vh] w-auto max-w-full rounded-2xl object-contain"
    />
  </div>
);

/**
 * Zwraca podgląd załącznika (player wideo lub podgląd PDF). Pliki bez podglądu
 * pobiera się przyciskiem z nagłówka karty, więc nie renderujemy tu nic.
 */
const AddonAttachmentRenderer: React.FC<{
  attachment: MappedAddonAttachment;
  vehicleDescription?: string;
}> = ({ attachment, vehicleDescription }) => {
  if (attachment.attachmentType === 'VIDEO' && attachment.previewUrl) {
    return (
      <AddonAttachmentVideo
        attachment={attachment}
        vehicleDescription={vehicleDescription}
      />
    );
  }

  if (attachment.contentType?.startsWith('image/') && attachment.previewUrl) {
    return <AddonAttachmentImage attachment={attachment} />;
  }

  const isPdf =
    attachment.attachmentType === 'DOCUMENT' ||
    attachment.attachmentType === 'SCAN_DOCUMENT' ||
    attachment.contentType === 'application/pdf';

  if (isPdf && attachment.previewUrl) {
    return <AddonAttachmentDocument attachment={attachment} />;
  }

  return null;
};

const isVideoAttachment = (attachment: MappedAddonAttachment) =>
  attachment.attachmentType === 'VIDEO';

const AddonCard: React.FC<{
  addon: MappedAddon;
  vehicleDescription?: string;
}> = ({ addon, vehicleDescription }) => {
  const presentation = getAddonPresentation(addon.type);
  const title = presentation.title || addon.typeDisplayName || addon.type;

  const isVideo = addon.attachments.some(isVideoAttachment);
  const primaryAttachment = addon.attachments.find((att) => att.downloadUrl);
  const downloadLabel = isVideo
    ? 'Pobierz film z oględzin'
    : 'Pobierz plik';

  return (
    <li
      id={`dodatkowe-uslugi-${addon.id}`}
      className="rounded-2xl border border-gray-200 bg-white p-4 lg:p-6"
    >
      <div
        className={clsx(
          'flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'
        )}
      >
        <div className="flex items-start gap-4">
          <div
            className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${presentation.iconBg}`}
          >
            <SVGIcon name={presentation.icon} size="md" fill={presentation.iconFill} />
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="text-lg font-bold text-text-primary">{title}</span>
            {presentation.description && (
              <span className="mt-1 text-sm text-grayText">
                {presentation.description}
              </span>
            )}
          </div>
        </div>

        {primaryAttachment?.downloadUrl && (
          <DownloadButton
            url={primaryAttachment.downloadUrl}
            filename={primaryAttachment.displayName}
            label={downloadLabel}
          />
        )}
      </div>

      {addon.attachments.length > 0 && (
        <ul className="mt-5 flex flex-col gap-5">
          {addon.attachments.map((att) => (
            <li key={att.id} className="empty:hidden">
              <AddonAttachmentRenderer
                attachment={att}
                vehicleDescription={vehicleDescription}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export const ReportAddons: React.FC<ReportAddonsProps> = ({
  addons,
  vehicleDescription,
}) => (
  <div className="mt-6 space-y-8 lg:mt-10">
    <ul className="flex flex-col gap-5">
      {addons.map((addon) => (
        <AddonCard
          key={addon.id}
          addon={addon}
          vehicleDescription={vehicleDescription}
        />
      ))}
    </ul>
  </div>
);
